import { useState } from "react";
import axios from "axios";
import {
  Mail, Phone, MapPin, ArrowRight, Check, Loader2, AlertCircle,
  ShieldCheck, Clock, Globe2, Building2, Users, FileText,
} from "lucide-react";
import { BRAND, LANGUAGE_SERVICES, AI_SERVICES } from "@/data/content";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  ...LANGUAGE_SERVICES.map((s) => s.title),
  ...AI_SERVICES.map((s) => s.shortTitle || s.title),
  "Other / Not sure",
];

const TRUST_POINTS = [
  { icon: ShieldCheck, label: "NDA & confidentiality on every project" },
  { icon: Clock, label: "Reply within 2 business hours" },
  { icon: Globe2, label: "Native experts in 85+ languages" },
];

// Approximate global linguist hubs (relative % positions on a 1000x500 map plate)
const HUBS = [
  { x: 28.5, y: 34, label: "New York HQ", primary: true },
  { x: 22, y: 56, label: "Mexico City" },
  { x: 34, y: 70, label: "São Paulo" },
  { x: 48, y: 30, label: "London" },
  { x: 51, y: 34, label: "Paris" },
  { x: 48, y: 38, label: "Madrid" },
  { x: 53, y: 60, label: "Lagos" },
  { x: 58, y: 66, label: "Nairobi" },
  { x: 62, y: 44, label: "Dubai" },
  { x: 67, y: 52, label: "Mumbai" },
  { x: 78, y: 42, label: "Beijing" },
  { x: 84, y: 44, label: "Tokyo" },
  { x: 86, y: 78, label: "Sydney" },
];

const INITIAL = { name: "", company: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        message: res.data.message || "Thank you. We'll respond within 2 business hours.",
      });
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setStatus({ state: "error", message: detail });
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <Seo
        title="Contact Us"
        description="Get a free quote in under 2 business hours. Talk to Petraloc about translation, localization, or AI-powered language solutions."
        path="/contact"
      />

      {/* COMPACT HERO */}
      <section className="relative pt-16 lg:pt-[68px] bg-[#071D49] text-white overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#00D1B2]/10 blur-3xl" />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 py-12 lg:py-14">
          <Reveal variant="up">
            <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#00D1B2]">
              Contact
            </p>
            <h1 className="mt-3 text-3xl lg:text-[44px] font-semibold tracking-tight leading-[1.05] max-w-3xl text-white">
              Start a conversation. Get a quote in 2 hours.
            </h1>
            <p className="mt-4 text-[14.5px] text-white/75 leading-relaxed max-w-2xl">
              Tell us about your project — language pair, content type, deadline. A Petraloc
              specialist will respond with a transparent quote within two business hours.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="pt-12 lg:pt-16 pb-10 lg:pb-12 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <Reveal variant="right" className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-[#E5E7EB] rounded-md p-6 lg:p-8"
            >
              <h2 className="text-2xl font-semibold text-[#071D49] tracking-tight">
                Request a quote
              </h2>
              <p className="mt-1.5 text-sm text-[#5B6472]">All fields marked with * are required.</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[12px] font-semibold text-[#071D49] block mb-1.5">Full name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name"
                    className="w-full bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#1A4E8A]"
                  />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#071D49] block mb-1.5">Company</label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    data-testid="contact-company"
                    className="w-full bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#1A4E8A]"
                  />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#071D49] block mb-1.5">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email"
                    className="w-full bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#1A4E8A]"
                  />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#071D49] block mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-phone"
                    className="w-full bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#1A4E8A]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[12px] font-semibold text-[#071D49] block mb-1.5">Service</label>
                  <select
                    value={form.service}
                    onChange={update("service")}
                    data-testid="contact-service"
                    className="w-full bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#1A4E8A]"
                  >
                    <option value="">Select a service…</option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[12px] font-semibold text-[#071D49] block mb-1.5">Project details *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your project — language pair, content type, word count, deadline…"
                    data-testid="contact-message"
                    className="w-full bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#1A4E8A] resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="contact-submit"
                className="mt-6 btn-cta btn-navy w-full sm:w-auto justify-center"
              >
                {status.state === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Inquiry <ArrowRight className="btn-arrow w-4 h-4" />
                  </>
                )}
              </button>

              {status.state === "success" && (
                <div
                  data-testid="contact-success"
                  className="mt-5 flex items-start gap-3 p-4 bg-[#00D1B2]/10 border border-[#00D1B2]/30 rounded-md"
                >
                  <Check className="w-5 h-5 text-[#00D1B2] flex-shrink-0 mt-0.5" />
                  <p className="text-[13.5px] text-[#071D49]">{status.message}</p>
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid="contact-error"
                  className="mt-5 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md"
                >
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[13.5px] text-red-800">{status.message}</p>
                </div>
              )}
            </form>
          </Reveal>

          {/* Side info */}
          <aside className="lg:col-span-5 space-y-4">
            <Reveal variant="left" delay={1}>
              <div className="ent-card p-6">
                <h3 className="text-[15px] font-semibold text-[#071D49] mb-4">Reach us directly</h3>
                <div className="space-y-3 text-[13.5px]">
                  <a href={`mailto:${BRAND.email}`} className="flex items-start gap-3 text-[#222222] hover:text-[#1A4E8A] transition-colors">
                    <Mail className="w-4 h-4 mt-0.5 text-[#1A4E8A] flex-shrink-0" />
                    <span>{BRAND.email}</span>
                  </a>
                  <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="flex items-start gap-3 text-[#222222] hover:text-[#1A4E8A] transition-colors">
                    <Phone className="w-4 h-4 mt-0.5 text-[#1A4E8A] flex-shrink-0" />
                    <span>{BRAND.phone}</span>
                  </a>
                  <div className="flex items-start gap-3 text-[#222222]">
                    <Building2 className="w-4 h-4 mt-0.5 text-[#1A4E8A] flex-shrink-0" />
                    <span>800 Third Avenue, New York, NY 10022, USA</span>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal variant="left" delay={2}>
              <div className="ent-card p-6">
                <h3 className="text-[15px] font-semibold text-[#071D49] mb-4">Why Petraloc</h3>
                <ul className="space-y-3">
                  {TRUST_POINTS.map((p) => (
                    <li key={p.label} className="flex items-start gap-3 text-[13.5px] text-[#222222]">
                      <p.icon className="w-4 h-4 mt-0.5 text-[#00D1B2] flex-shrink-0" />
                      {p.label}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal variant="left" delay={3}>
              <div className="bg-[#071D49] text-white p-6 rounded-md">
                <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#00D1B2] mb-2">
                  Enterprise scale?
                </p>
                <p className="text-[13.5px] text-white/85 leading-relaxed">
                  For volume contracts, RFPs, or managed-service engagements, mention "enterprise"
                  in your message and we'll route you to our enterprise team.
                </p>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      {/* LINGUIST APPLICATION — careers / vendor block */}
      <section
        className="pb-14 lg:pb-16 bg-white"
        data-testid="linguist-application"
        aria-labelledby="linguist-heading"
      >
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up">
            <div className="relative overflow-hidden rounded-md border border-[#E5E7EB] bg-gradient-to-br from-[#F7F8FA] via-white to-[#EEF1F6]">
              {/* Teal corner glow */}
              <div className="pointer-events-none absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-[#00D1B2]/10 blur-3xl" />
              {/* Navy left accent bar */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#1A4E8A] via-[#00D1B2] to-[#1A4E8A]" />

              <div className="relative grid lg:grid-cols-12 gap-8 p-6 lg:p-10">
                {/* Left: icon + headline + body */}
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#071D49] text-[#00D1B2]">
                      <Users className="w-[18px] h-[18px]" strokeWidth={2} />
                    </span>
                    <p className="eyebrow-teal !mt-0">Careers · Linguist Network</p>
                  </div>

                  <h2
                    id="linguist-heading"
                    className="mt-4 text-3xl lg:text-[36px] font-semibold tracking-tight text-[#071D49] leading-[1.1]"
                  >
                    Join Our Global Linguist Network
                  </h2>

                  <p className="mt-4 text-[14.5px] text-[#5B6472] leading-relaxed max-w-2xl">
                    Are you a translator, interpreter, proofreader, subtitler, or localization
                    specialist? Petraloc is always looking for talented linguists worldwide to join
                    our growing network. If you'd like to collaborate on multilingual projects,
                    send your CV and language pairs to:
                  </p>
                </div>

                {/* Right: email card */}
                <div className="lg:col-span-5 flex">
                  <div
                    className="w-full bg-white border border-[#E5E7EB] rounded-md p-5 lg:p-6 flex flex-col justify-center shadow-[0_1px_2px_rgba(7,29,73,0.04)]"
                    data-testid="linguist-email-card"
                  >
                    <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#5B6472]">
                      Submit your application
                    </p>
                    <a
                      href="mailto:recruiting@petraloc.com?subject=Linguist%20Application%20%E2%80%94%20Petraloc"
                      data-testid="linguist-email-link"
                      className="group mt-2 inline-flex items-center gap-2.5 text-[#071D49] hover:text-[#1A4E8A] transition-colors"
                    >
                      <Mail className="w-[18px] h-[18px] text-[#1A4E8A] group-hover:text-[#00D1B2] transition-colors flex-shrink-0" />
                      <span className="text-[18px] lg:text-[20px] font-semibold tracking-tight underline decoration-[#00D1B2]/40 underline-offset-4 group-hover:decoration-[#00D1B2] break-all">
                        recruiting@petraloc.com
                      </span>
                    </a>
                    <div className="mt-4 flex items-start gap-2 text-[12.5px] text-[#5B6472] leading-relaxed">
                      <FileText className="w-3.5 h-3.5 mt-0.5 text-[#1A4E8A] flex-shrink-0" />
                      <p>
                        Please include your CV/resume, language pairs, areas of specialization, and
                        relevant experience.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GLOBAL PRESENCE MAP */}
      <section className="py-14 lg:py-16 bg-[#F7F8FA] border-t border-[#E5E7EB]" data-testid="global-presence">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <Reveal variant="right" className="lg:col-span-4">
              <p className="eyebrow-teal">Global Presence</p>
              <h2 className="mt-3 text-3xl lg:text-[36px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
                Headquartered in New York. Operating worldwide.
              </h2>
              <p className="mt-4 text-[14.5px] text-[#5B6472] leading-relaxed">
                Petraloc operates a global linguist network with verified native specialists
                across the Americas, Europe, Africa, the Middle East, and Asia-Pacific.
              </p>
              <ul className="mt-6 space-y-2.5 text-[13.5px] text-[#222222]">
                <li className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[#00D1B2] animate-dot-pulse" />
                  <span><strong className="text-[#071D49]">New York HQ</strong> — U.S. operations & enterprise sales</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[#1A4E8A]" />
                  <span>13+ regional delivery hubs</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[#1A4E8A]" />
                  <span>450+ native linguists across 85+ languages</span>
                </li>
              </ul>
            </Reveal>

            <Reveal variant="left" className="lg:col-span-8">
              <div className="relative aspect-[2/1] bg-gradient-to-br from-[#F7F8FA] via-white to-[#EEF1F6] border border-[#E5E7EB] rounded-md overflow-hidden">
                {/* Clean inline SVG world map silhouette */}
                <svg
                  viewBox="0 0 1000 500"
                  className="absolute inset-0 w-full h-full"
                  preserveAspectRatio="xMidYMid meet"
                  aria-hidden="true"
                >
                  <defs>
                    {/* Continent fill gradient — premium subtle tone */}
                    <linearGradient id="continent-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#CFD7E3" />
                      <stop offset="100%" stopColor="#B8C2D2" />
                    </linearGradient>
                    {/* HQ radial glow */}
                    <radialGradient id="hq-glow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#00D1B2" stopOpacity="0.55" />
                      <stop offset="60%" stopColor="#00D1B2" stopOpacity="0.18" />
                      <stop offset="100%" stopColor="#00D1B2" stopOpacity="0" />
                    </radialGradient>
                    {/* Regional pin glow */}
                    <radialGradient id="hub-glow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#1A4E8A" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#1A4E8A" stopOpacity="0" />
                    </radialGradient>
                    {/* Soft equator vignette */}
                    <radialGradient id="map-vignette" cx="50%" cy="50%" r="65%">
                      <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
                      <stop offset="100%" stopColor="#071D49" stopOpacity="0.05" />
                    </radialGradient>
                  </defs>

                  <rect width="1000" height="500" fill="url(#map-vignette)" />

                  {/* Continent silhouettes — solid subtle-tone world map from uploaded reference */}
                  <image
                    href="/world-map-silhouette.png"
                    x="0"
                    y="0"
                    width="1000"
                    height="500"
                    preserveAspectRatio="xMidYMid meet"
                    opacity="0.95"
                  />

                  {/* HQ outer glow halo (rendered behind dots) */}
                  <circle
                    cx={(HUBS[0].x / 100) * 1000}
                    cy={(HUBS[0].y / 100) * 500}
                    r="48"
                    fill="url(#hq-glow)"
                  />

                  {/* Regional hub soft glows */}
                  {HUBS.filter((h) => !h.primary).map((h) => (
                    <circle
                      key={`glow-${h.label}`}
                      cx={(h.x / 100) * 1000}
                      cy={(h.y / 100) * 500}
                      r="22"
                      fill="url(#hub-glow)"
                    />
                  ))}

                  {/* Connection lines from New York HQ to other hubs */}
                  {HUBS.filter((h) => !h.primary).map((h) => (
                    <line
                      key={`line-${h.label}`}
                      x1={(HUBS[0].x / 100) * 1000}
                      y1={(HUBS[0].y / 100) * 500}
                      x2={(h.x / 100) * 1000}
                      y2={(h.y / 100) * 500}
                      stroke="#00D1B2"
                      strokeOpacity="0.32"
                      strokeWidth="0.8"
                      strokeDasharray="2 5"
                    />
                  ))}
                </svg>

                {/* Hub markers (HTML overlay for crisp ring + label) */}
                {HUBS.map((h) => (
                  <div
                    key={h.label}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                    style={{ left: `${h.x}%`, top: `${h.y}%` }}
                    data-testid={`map-hub-${h.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {h.primary ? (
                      <>
                        <span className="absolute inset-0 -m-3 rounded-full bg-[#00D1B2]/30 animate-dot-pulse" />
                        <span className="absolute inset-0 -m-1.5 rounded-full bg-[#00D1B2]/50" />
                        <span className="relative block w-3.5 h-3.5 rounded-full bg-[#00D1B2] ring-2 ring-white shadow-[0_0_12px_rgba(0,209,178,0.7)]" />
                      </>
                    ) : (
                      <>
                        <span className="absolute inset-0 -m-1 rounded-full bg-[#1A4E8A]/30 animate-dot-pulse" />
                        <span className="relative block w-2.5 h-2.5 rounded-full bg-[#1A4E8A] ring-1.5 ring-white shadow-[0_0_8px_rgba(26,78,138,0.55)]" />
                      </>
                    )}
                    <span
                      className={`absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide ${
                        h.primary
                          ? "bg-[#071D49] text-white"
                          : "bg-white/95 text-[#071D49] opacity-0 group-hover:opacity-100 transition-opacity shadow-sm border border-[#E5E7EB]"
                      }`}
                    >
                      {h.label}
                    </span>
                  </div>
                ))}

                {/* Legend */}
                <div className="absolute bottom-3 right-3 flex items-center gap-4 bg-white/95 backdrop-blur border border-[#E5E7EB] rounded px-3 py-1.5 text-[11px] font-medium text-[#071D49]">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#00D1B2] shadow-[0_0_8px_rgba(0,209,178,0.6)]" /> HQ
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#1A4E8A]" /> Regional hub
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
